import { Action, Reducer } from 'redux';
import { delay } from 'redux-saga/effects';
import { fork, call, takeEvery, put, all, select } from 'redux-saga/effects';

import { replace } from 'connected-react-router';

import { ITableConfig } from '../components/common/table';

import Api from '../api';

import { IRootState } from './index';
import User from '../models/User';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';

import PagedResultSet from '../models/common/PagedResultSet';
import { ODataOptions } from '../util/ODataBuilder';
import IdentifyDto from '../models/IdentityDto';
import UserRole from 'src/models/UserRole';
import ProjectAdministrationType from 'src/models/ProjectAdministrationType';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import ProjectStaff from 'src/models/ProjectStaff';

// Store
export interface IUserState {
    isLoadingUsers: boolean;
    users: PagedResultSet<User> | undefined;
    allUsers: Array<User>;
    adminUsersPageTableConfig: ITableConfig;
    roles: Array<UserRole>;
    projectAdministrationTypes: Array<ProjectAdministrationType>;
    selectedUser: User;
    duplicateUser: boolean;
    errorMessage: string;
}

const initialState: IUserState = {
    isLoadingUsers: false,
    users: undefined,
    allUsers: undefined,
    adminUsersPageTableConfig: {
        rowCount: 20,
        pageNumber: 1,
        idField: 'id',
        columns: [
            { name: 'Username', mapping: 'username' },
            { name: 'First Name', mapping: 'firstName' },
            { name: 'Last Name', mapping: 'lastName' },
            { name: 'Type', mapping: 'type.name' },
            { name: 'Role', mapping: 'role.role' },
        ],
    },
    roles: undefined,
    projectAdministrationTypes: undefined,
    selectedUser: undefined,
    duplicateUser: false,
    errorMessage: undefined
};

// Action Types
export enum actionTypes {
    GET_USERS = '[user] GET_USERS',
    GET_USERS_FOR_PAGE = '[user] GET_USERS_FOR_PAGE',
    GET_USER_BY_ID = '[user] GET_USER_BY_ID',
    INCREMENT_USERS = '[users] INCREMENT_USERS',
    GET_USERS_SUCCESS = '[company] GET_USERS_SUCCESS',
    GET_USERS_FOR_PAGE_SUCCESS = '[company] GET_USERS_FOR_PAGE_SUCCESS',
    GET_USER_BY_ID_SUCCESS = '[user] GET_USER_BY_ID_SUCCESS',
    CREATE_USER = '[user] CREATE_USER',
    EDIT_USER = '[user] EDIT_USER',
    CREATE_USER_SUCCESS = '[user] CREATE_USER_SUCCESS',
    DUPLICATE_USER_FOUND = '[user] DUPLICATE_USER_FOUND',
    EDIT_USER_SUCCESS = '[user] EDIT_USER_SUCCESS',
    GET_ROLES = '[user] GET_ROLES',
    GET_ROLES_SUCCESS = '[users] GET_ROLES_SUCCESS',
    GET_USERS_BY_OPTIONS = '[users] GET_USERS_BY_OPTIONS',
    GET_USERS_BY_OPTIONS_SUCCESS = '[users] GET_USERS_BY_OPTIONS_SUCCESS',
    INITIALIZE_USERS = '[users] INITIALIZE_USERS',
    GET_PROJECT_ADMINISTRATION_TYPES = '[users] GET_PROJECT_ADMINISTRATION_TYPES',
    GET_PROJECT_ADMINISTRATION_TYPES_SUCCESS = '[users] GET_PROJECT_ADMINISTRATION_TYPES_SUCCESS',
    ADD_PROJECT_ADMINISTRATION_STAFF = '[users] ADD_PROJECT_ADMINISTRATION_STAFF',
    ADD_PROJECT_ADMINISTRATION_STAFF_SUCCESS = '[users] ADD_PROJECT_ADMINISTRATION_STAFF_SUCCESS',
    ADD_LANDMAN_STAFF = '[users] ADD_LANDMAN_STAFF',
    ADD_LANDMAN_STAFF_SUCCESS = '[users] ADD_LANDMAN_STAFF_SUCCESS'
}

// Action Creator
export const actionCreators = {
    getUsers: (resetPage?: boolean) => {
        return { type: actionTypes.GET_USERS, payload: { resetPage } };
    },
    getUsersForPage: (options: ODataOptions, resetPage?: boolean) => {
        return { type: actionTypes.GET_USERS_FOR_PAGE, payload: {options, resetPage } };
    },
    getUserById: (id: number) => {
        return { type: actionTypes.GET_USER_BY_ID, payload: id};
    },
    incrementUsers: () => {
        return { type: actionTypes.INCREMENT_USERS };
    },
    getUsersSuccess: (users: PagedResultSet<User>, resetPage: boolean) => {
        return { type: actionTypes.GET_USERS_SUCCESS, payload: {users, resetPage} };
    },
    getUsersForPageSuccess: (users: PagedResultSet<User>, resetPage: boolean) => {
        return { type: actionTypes.GET_USERS_FOR_PAGE_SUCCESS, payload: {users, resetPage} };
    },
    getUserByIdSuccess: (user: User) => {
        return { type: actionTypes.GET_USER_BY_ID_SUCCESS, payload: user };
    },
    createUser: (user: User) => {
        return { type: actionTypes.CREATE_USER, payload: user };
    },
    editUser: (user: User) => {
        return { type: actionTypes.EDIT_USER, payload: user };
    },
    createUserSuccess: (userId: IdentifyDto) => {
        return { type: actionTypes.CREATE_USER_SUCCESS, payload: userId };
    },
    duplicateUserFound: (errorMessage: string) => {
        return { type: actionTypes.DUPLICATE_USER_FOUND, payload: errorMessage };
    },
    editUserSuccess: (userId: IdentifyDto) => {
        return { type: actionTypes.EDIT_USER_SUCCESS, payload: userId };
    },
    getRoles: () => {
        return { type: actionTypes.GET_ROLES };
    },
    getRolesSuccess: (roles: Array<ProjectAdministrationType>) => {
        return { type: actionTypes.GET_ROLES_SUCCESS, payload: roles };
    },
    getProjectAdministrationTypes: () => {
        return { type: actionTypes.GET_PROJECT_ADMINISTRATION_TYPES };
    },
    getProjectAdministrationTypesSuccess: (roles: Array<ProjectAdministrationType>) => {
        return { type: actionTypes.GET_PROJECT_ADMINISTRATION_TYPES_SUCCESS, payload: roles };
    },
    getUsersByOptions: (options: ODataOptions) => {
        return { type: actionTypes.GET_USERS_BY_OPTIONS, payload: options };
    },

    getUsersByOptionsSuccess: (users: PagedResultSet<User>) => {
        return { type: actionTypes.GET_USERS_BY_OPTIONS_SUCCESS, payload: users };
    },
    addProjectAdministrationStaff: (clientNumber: number, projectNumber: number, staffInfo: ProjectAdministratorStaff) => {
        return { type: actionTypes.ADD_PROJECT_ADMINISTRATION_STAFF, payload: { clientNumber, projectNumber, staffInfo } };
    },
    addProjectAdministrationStaffSuccess: (id: number) => {
        return { type: actionTypes.ADD_PROJECT_ADMINISTRATION_STAFF_SUCCESS, payload: id };
    },
    addLandmanStaff: (clientNumber: number, projectNumber: number, staffInfo: ProjectStaff) => {
        return { type: actionTypes.ADD_LANDMAN_STAFF, payload: { clientNumber, projectNumber, staffInfo } };
    },
    addLandmanStaffSuccess: (id: number) => {
        return { type: actionTypes.ADD_LANDMAN_STAFF_SUCCESS, payload: id };
    },
    initUsers: () => {
        return { type: actionTypes.INITIALIZE_USERS };
    },
};

// Reducer
const reducer: Reducer<IUserState> = (state: IUserState = initialState, action: Action): IUserState => {
    switch (action.type) {
        case actionTypes.GET_USERS:
            return {
                ...state,
                users: initialState.users,
                isLoadingUsers: true,
            };
        case actionTypes.GET_USERS_SUCCESS:
            const getUsersPayload = (action as { type: string; payload: {users: Array<User>, resetPage: boolean } }).payload;
            return {
                ...state,
                allUsers: getUsersPayload.users,
                isLoadingUsers: false,
            };
        case actionTypes.GET_USERS_FOR_PAGE_SUCCESS:
            const getUsersForPagePayload = (action as { type: string; payload: {users: PagedResultSet<User>, resetPage: boolean } }).payload;
            if (getUsersForPagePayload.resetPage) {
                return {
                    ...state,
                    users: getUsersForPagePayload.users,
                    isLoadingUsers: false,
                };
            } else if (state.users === undefined || state.users.pageNumber < getUsersForPagePayload.users.pageNumber) {
                const newUsers = {...state.users };
                newUsers.pageNumber = getUsersForPagePayload.users.pageNumber;
                newUsers.pageSize = getUsersForPagePayload.users.pageSize;
                newUsers.totalRecords = getUsersForPagePayload.users.totalRecords;
                if (!newUsers.items) {
                    newUsers.items = [];
                }
                newUsers.items = newUsers.items.concat(getUsersForPagePayload.users.items);
                return {
                    ...state,
                    users: newUsers,
                    isLoadingUsers: false
                };
            }

            return state;
        case actionTypes.INCREMENT_USERS:
            return {
                ...state,
                adminUsersPageTableConfig: {
                    ...state.adminUsersPageTableConfig,
                    pageNumber: state.adminUsersPageTableConfig.pageNumber + 1
                }
            };
        case actionTypes.GET_USER_BY_ID_SUCCESS:
            const getUserByIdPayload = (action as { type: string; payload: User }).payload;
            return {
                ...state,
                selectedUser: getUserByIdPayload
            };
        case actionTypes.DUPLICATE_USER_FOUND:
            const duplicateUserPayload = (action as { type: string; payload: string }).payload;
            return {
                ...state,
                duplicateUser: true,
                errorMessage: duplicateUserPayload
            };
        case actionTypes.GET_ROLES_SUCCESS:
            const getRolesPayload = (action as { type: string; payload: Array<UserRole> }).payload;
            return {
                ...state,
                roles: getRolesPayload,
            };
        case actionTypes.GET_PROJECT_ADMINISTRATION_TYPES_SUCCESS:
            const getProjectAdminTypesPayload = (action as { type: string; payload: Array<ProjectAdministrationType> }).payload;
            return {
                ...state,
                projectAdministrationTypes: getProjectAdminTypesPayload,
            };
        case actionTypes.GET_USERS_BY_OPTIONS_SUCCESS:
            const getUsersByOptionsPayload = (action as { type: string; payload: PagedResultSet<User> }).payload;
            return {
                ...state,
                users: getUsersByOptionsPayload,
            };
        case actionTypes.INITIALIZE_USERS:
            return {
                ...state,
                users: undefined,
                selectedUser: undefined,
                adminUsersPageTableConfig: {
                    ...state.adminUsersPageTableConfig,
                    pageNumber: 1
                }
            };
        default:
            return state;
    }
};

export default reducer;

// Selectors
const usersSelector = (state: IRootState): PagedResultSet<User> | undefined => state.user.users;
const pcapmSelector = (state: IRootState): Array<User> | undefined => state.user.allUsers?.filter(au => au.role != null && au.role.role === 'Project Coordinator');

export const selectors = {
    isLoadingUsers: (state: IRootState): boolean => state.user.isLoadingUsers || state.layout.initialLoading,
    users: usersSelector,
    pcapmUsers: pcapmSelector,
    adminUsersPageTableConfig: (state: IRootState): ITableConfig => state.user.adminUsersPageTableConfig,
};

// Side-Effects
const sagas = {
    getUsers: function* (action: { type: string; payload: { resetPage: boolean }}): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.User.getAll, jwt), delay(200)]);
        yield put(actionCreators.getUsersSuccess(result, action.payload?.resetPage));
    },
    getUsersForPage: function* (action: { type: string; payload: { options: ODataOptions, resetPage: boolean }}): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const tableConfig = yield select(selectors.adminUsersPageTableConfig);
        const options = ODataOptions.from(tableConfig);
        if (action.payload) {
            options.merge(action.payload.options);
        }

        const [result] = yield all([call(Api.User.getPaged, options, jwt), delay(200)]);
        yield put(actionCreators.getUsersForPageSuccess(result, action.payload?.resetPage));
    },
    getUserById: function* (action: { type: string; payload: number }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const userId = (action as { type: string; payload: number }).payload;
        const [result] = yield all([call(Api.User.get, userId, jwt), delay(200)]);
        yield put(actionCreators.getUserByIdSuccess(result));
    },
    createUser: function* (action: { type: string; payload: User }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const user = (action as { type: string; payload: User }).payload;
        const [result] = yield all([call(Api.User.create, user, jwt), delay(200)]);
        console.log('Create User Result:', result);
        if (result.isError === true && result.messageCode === '2') {
            yield put(actionCreators.duplicateUserFound(result.message));
        } else {
            yield put(replace('/admin/users'));   
        }
    },
    editUser: function* (action: { type: string; payload: User }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const user = (action as { type: string; payload: User }).payload;
        yield all([call(Api.User.edit, user, jwt), delay(200)]);
        yield put(replace('/admin/users'));
    },
    getRoles: function* (action: { type: string }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.User.getRoles, jwt), delay(200)]);
        yield put(actionCreators.getRolesSuccess(result));
    },
    getProjectAdministrationTypes: function* (action: { type: string }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.User.getProjectAdministrationTypes, jwt), delay(200)]);
        yield put(actionCreators.getProjectAdministrationTypesSuccess(result));
    },
    getUsersByOptions: function* (action: { type: string; payload: ODataOptions }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.User.getPaged, action.payload, jwt), delay(200)]);
        yield put(actionCreators.getUsersByOptionsSuccess(result));
    },
    addUserToStaff: function* (action: {
        type: string;
        payload: { clientNumber: number; projectNumber: number; staffInfo: ProjectAdministratorStaff };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        // action.payload.id = 0;
        const [result] = yield all([call(Api.User.addUserToProjectAdministration, action.payload, jwt), delay(200)]);
        yield put(actionCreators.addProjectAdministrationStaffSuccess(result));
    },
    addLandmanToStaff: function* (action: {
        type: string;
        payload: { clientNumber: number; projectNumber: number; staffInfo: ProjectStaff };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        // action.payload.id = 0;
        const [result] = yield all([call(Api.User.addLandmanToStaff, action.payload, jwt), delay(200)]);
        yield put(actionCreators.addLandmanStaffSuccess(result));
    },
};

const watches = {
    getUsers: function* () {
        yield takeEvery(actionTypes.GET_USERS, sagas.getUsers);
    },
    getUsersForPage: function* () {
        yield takeEvery(actionTypes.GET_USERS_FOR_PAGE, sagas.getUsersForPage);
    },
    getUserById: function* () {
        yield takeEvery(actionTypes.GET_USER_BY_ID, sagas.getUserById);
    },
    incrementUsers: function* () {
        yield takeEvery(actionTypes.INCREMENT_USERS, sagas.getUsersForPage);
    },
    createUser: function* () {
        yield takeEvery(actionTypes.CREATE_USER, sagas.createUser);
    },
    editUser: function* () {
        yield takeEvery(actionTypes.EDIT_USER, sagas.editUser);
    },
    getRoles: function* () {
        yield takeEvery(actionTypes.GET_ROLES, sagas.getRoles);
    },
    getUsersByOptions: function* () {
        yield takeEvery(actionTypes.GET_USERS_BY_OPTIONS, sagas.getUsersByOptions);
    },
    getProjectAdministrationTypes: function* () {
        yield takeEvery(actionTypes.GET_PROJECT_ADMINISTRATION_TYPES, sagas.getProjectAdministrationTypes);
    },
    addUserToStaff: function* () {
        yield takeEvery(actionTypes.ADD_PROJECT_ADMINISTRATION_STAFF, sagas.addUserToStaff);
    },
    addLandmanStaff: function* () {
        yield takeEvery(actionTypes.ADD_LANDMAN_STAFF, sagas.addLandmanToStaff);
    },
};

export function* saga() {
    yield fork(watches.getUsers);
    yield fork(watches.getUsersForPage);
    yield fork(watches.getUserById);
    yield fork(watches.incrementUsers);
    yield fork(watches.createUser);
    yield fork(watches.editUser);
    yield fork(watches.getRoles);
    yield fork(watches.getUsersByOptions);
    yield fork(watches.getProjectAdministrationTypes);
    yield fork(watches.addUserToStaff);
    yield fork(watches.addLandmanStaff);
}



// WEBPACK FOOTER //
// ./src/reducers/User.ts